import { DateTime } from 'luxon'

import { ShiftOfferStatus } from '../../employee_shift/models/employeeShiftOffer'
import type { RequestQueue, RequestRound, RoundOffer } from '../../shifts/models/requestQueue'

/**
 * Pure functions that turn the queue into sentences. The view only lays
 * these out, so everything a manager reads is testable without a DOM.
 */

/** Fixed locale so the wording is the same in every browser and in tests. Change LOCALE if you localise. */
export const LOCALE = 'en-GB'

export const clock = (d: DateTime, now: DateTime): string => {
  const time = d.toLocaleString(DateTime.TIME_SIMPLE)
  return d.hasSame(now, 'minute')
    ? time
    : `${d.toLocaleString({ weekday: 'short' })} ${time}`
  //   const time = d.toLocaleTimeString(LOCALE, { hour: '2-digit', minute: '2-digit' })
  //   return d.toDateString() === now.toDateString() ? time : `${d.toLocaleDateString(LOCALE, { weekday: 'short' })} ${time}`
}

const list = (names: string[]): string =>
  new Intl.ListFormat(LOCALE, { type: 'conjunction' }).format(names)

const people = (n: number) => (n === 1 ? '1 person' : `${n} people`)

export const isLive = (round: RequestRound): boolean =>
  round.allEligible || round.tags.length > 0

export const tagText = (round: RequestRound): string =>
  round.label ??
  round.tags.map((t) => t.name).join(round.tagMatch === 'all' ? ' + ' : ' or ')

/** Who the round is for, as configured. */
export function who(round: RequestRound): string {
  const named = round.employees.map((p) => p.name)
  if (!isLive(round)) return list(named)

  const rule = round.allEligible
    ? 'everyone eligible'
    : `everyone tagged ${tagText(round)}`
  const except = round.excludedEmployees.length
    ? ` except ${list(round.excludedEmployees.map((p) => p.name))}`
    : ''
  const plus = named.length ? `, plus ${list(named)}` : ''
  return `${rule}${except}${plus}`
}

/** "Sent 09:00", "Sends 09:30", "Skipped: nobody was eligible", "Cancelled". */
export function timing(
  round: RequestRound,
  now: DateTime,
): { text: string; tone: 'done' | 'next' | 'later' | 'off' } {
  switch (round.status) {
    case 'fired':
      return { text: `Sent ${clock(round.firedAt!, now)}`, tone: 'done' }
    case 'skipped':
      return {
        text:
          round.skippedReason === 'no_eligible_employees'
            ? 'Skipped: nobody was eligible'
            : 'Skipped',
        tone: 'off',
      }
    case 'cancelled':
      return { text: 'Cancelled', tone: 'off' }
    case 'pending': {
      const at = round.firesAt ?? undefined // round.projected_fires_at
      if (!at) return { text: 'Not started', tone: 'later' }
      return round.firesAt
        ? { text: `Sends ${clock(at, now)}`, tone: 'next' }
        : { text: `About ${clock(at, now)}`, tone: 'later' }
    }
  }
}

const order: ShiftOfferStatus[] = [
  'accepted',
  'applied',
  'pending',
  'declined',
  'rejected',
  'queued',
]
const wording: Record<ShiftOfferStatus, (n: number) => string> = {
  accepted: (n) => `${n} accepted`,
  applied: (n) => `${n} applied`,
  pending: (n) => `${n} awaiting reply`,
  declined: (n) => `${n} declined`,
  rejected: (n) => `${n} rejected`,
  queued: (n) => `${n} queued`,
}

/** "3 offered: 1 accepted, 1 declined, 1 awaiting reply" */
export function tally(offers: RoundOffer[]): string {
  if (offers.length === 0) return 'No offers'
  const counts = new Map<ShiftOfferStatus, number>()
  offers.forEach((o) => counts.set(o.status, (counts.get(o.status) ?? 0) + 1))
  const parts = order
    .filter((s) => counts.has(s))
    .map((s) => wording[s](counts.get(s)!))
  return `${offers.length} offered: ${parts.join(', ')}`
}

/** What a pending round would do if it sent now. */
export function preview(round: RequestRound): string | null {
  if (round.status !== 'pending' || !round.livePreview) return null
  const { count, sample } = round.livePreview
  if (count === 0)
    return isLive(round)
      ? "Nobody matches right now. If that's still true when it sends, it's skipped."
      : 'Nobody here is eligible right now, so this round would be skipped.'
  if (!isLive(round))
    return count === round.employees.length
      ? null
      : `${people(round.employees.length - count)} no longer eligible and would be skipped.`
  const names = sample.slice(0, 3).map((p) => p.name)
  const more = count - names.length
  if (names.length === 0) return `Right now that's ${people(count)}.`
  return `Right now that's ${more > 0 ? `${list(names)} and ${more} more` : list(names)}.`
}

export type QueueState =
  | 'not_started'
  | 'in_progress'
  | 'filled'
  | 'ended'
  | 'stopped'

/** The one line at the top. */
export function summary(
  queue: RequestQueue,
  now: DateTime,
): { state: QueueState; text: string } {
  const rounds = queue.rounds
  const accepted = rounds
    .flatMap((r) => r.offers)
    .find((o) => o.status === 'accepted')
  if (accepted) {
    const round = rounds.find((r) => r.offers.includes(accepted))!
    return {
      state: 'filled',
      text: `Filled by ${accepted.employee.name} from round ${round.position}.`,
    }
  }

  if (!queue.startedAt)
    return {
      state: 'not_started',
      text: `${rounds.length} rounds set up. Nothing has been sent yet.`,
    }

  const next = rounds.find((r) => r.status === 'pending')
  const sent = rounds.filter((r) => r.status === 'fired')
  const open = sent
    .flatMap((r) => r.offers)
    .filter((o) => o.status === 'pending').length

  if (next) {
    const at = next.firesAt
      ? ` Round ${next.position} sends ${clock(next.firesAt, now)}.`
      : ''
    const done = sent.length
      ? `${sent.length} of ${rounds.length} rounds sent, ${open} ${open === 1 ? 'offer' : 'offers'} awaiting reply.`
      : 'Starting.'
    return { state: 'in_progress', text: `${done}${at}` }
  }

  if (rounds.some((r) => r.status === 'cancelled'))
    return {
      state: 'stopped',
      text: 'The queue was stopped before every round was sent.',
    }
  return {
    state: 'ended',
    text: open
      ? `Every round has been sent. ${open} ${open === 1 ? 'offer is' : 'offers are'} still awaiting reply.`
      : 'Every round has been sent and nobody has accepted.',
  }
}
